import React from 'react';
import { getPreferredPaymentMethod, getUrlParam } from 'Core/utils/utils';
import useDepositForm from 'Modules/deposit/hooks/useDepositForm';
import CardSelector from 'Core/components/CardSelector';
import StyledFormGroup from 'Core/components/FormElements/StyledFormGroup';
import { SuccessButton } from 'Core/components/Button';
import { useTranslation } from 'react-i18next';
import { Column } from 'Core/components/Layout';
import Row from 'Core/components/Layout/Row';
import Card from 'Core/components/FormElements/Card';
import { formatCurrency } from 'Core/utils/numberFormat';
import Option from 'Core/components/Option/Option';
import TermsInput from 'Core/components/FormElements/TermsInput';
import Checkbox from 'Core/components/FormElements/Checkbox';
import InputField from 'Core/components/FormElements/InputField';

function LockIcon({ className = 'w-3.5 h-3.5' }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm2-2v2h6V7a3 3 0 00-6 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function SecurePaymentNote() {
  const { t } = useTranslation();
  return (
    <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500 mt-3 md:mt-0">
      <LockIcon />
      <span>{t('payment.securePaymentStripe', 'Secure payment method powered by Stripe')}</span>
    </p>
  );
}

function CheckoutButton({ disabled = false, depositType, amount }) {
  const { t } = useTranslation();
  const parsedAmount = amount ? Number(amount) : NaN;
  const formattedAmount =
    !Number.isNaN(parsedAmount) && parsedAmount >= 0
      ? formatCurrency(parsedAmount, { currency: 'USD', withDecimals: true })
      : null;
  const label =
    formattedAmount &&
    (depositType === 'HOLD'
      ? `Collect Hold - ${formattedAmount}`
      : `Complete Payment - ${formattedAmount}`);
  return (
    <SuccessButton
      block={true}
      type="submit"
      data-testid="checkout"
      size="tall"
      className="!shadow-md hover:!shadow-lg transition-shadow"
      disabled={disabled}
    >
      {label ?? t('common.checkout')}
    </SuccessButton>
  );
}

function StyledOptionItem({ children, isFirst, isLast }) {
  let borderRadiusClasses = 'border rounded-lg';
  if (isFirst) {
    borderRadiusClasses = 'rounded-l-lg rounded-r-lg border-y border-l';
  }
  if (isLast) {
    borderRadiusClasses = 'rounded-l-lg rounded-r-lg border-y border-r';
  }

  return (
    <div
      className={`shadow-md flex flex-col items-center justify-between bg-white border-gray-300 ${borderRadiusClasses} p-2 mt-2 mx-4 md:mx-0 w-full transition-colors hover:bg-gray-100 hover:text-primary hover:cursor-pointer`}
    >
      {children}
    </div>
  );
}

function AuthorizeOptions({ options, form, isExternalAccess = false }) {
  const { register, unregister, getValues, setValue } = form;
  const singleOption = options.length === 1;
  const [singleOptionData] = options;

  React.useEffect(() => {
    if (singleOption && singleOptionData) {
      setValue('amount', String(singleOptionData.amount));
    } else {
      setValue('amount', '98');
    }
    return () => unregister('amount');
  }, [setValue, unregister, singleOption, singleOptionData?.amount]);

  if (singleOption && singleOptionData) {
    return (
      <div data-testid="deposit-card-option-container" className="flex flex-col gap-2 mt-2">
        {isExternalAccess && (
          <p className="text-md font-bold text-gray-700">Processing a credit card hold of:</p>
        )}
        <div className="flex items-center">
          <div className="rounded-lg border-2 border-primary/30 bg-primary/5 px-4 py-3 w-full max-w-xs">
            <span className="text-2xl font-bold text-primary">
              {formatCurrency(singleOptionData.amount, { withDecimals: false })}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Row data-testid="deposit-card-option-container">
      {options.map(({ option, amount }, idx, arr) => (
        <div className="md:flex-col-4 flex-col-12" key={option}>
          <Option
            register={register}
            className="styled-deposit-option !flex"
            name="amount"
            value={amount}
            defaultChecked={getValues().amount === amount && getValues().type === 'authorize'}
          >
            <StyledOptionItem isFirst={idx === 0} isLast={idx === arr.length - 1}>
              <span className="text-2xl font-bold">
                {formatCurrency(amount, { withDecimals: false })}
              </span>
            </StyledOptionItem>
          </Option>
        </div>
      ))}
    </Row>
  );
}

function CaptureOption({ form }) {
  const { register, unregister, setValue } = form;
  React.useEffect(() => {
    setValue('amount', '');
    return () => unregister('amount');
  }, [setValue, unregister]);

  return (
    <Row>
      <Column className="md:flex-col-8">
        <InputField
          register={register}
          type="number"
          name={`amount`}
          error={form.formState.errors?.amount?.message}
          allowDecimals
          prependText={'$'}
        />
      </Column>
    </Row>
  );
}

const DepositOptions = ({ options, form, isExternalAccess = false }) => {
  const { t } = useTranslation();
  const { register, watch } = form;
  const type = watch('type');

  const cardMethods = [
    { id: 'HOLD', title: t('common.hold') },
    { id: 'CAPTURE', title: t('common.capture') },
  ];

  return (
    <StyledFormGroup>
      <Row className="">
        {!isExternalAccess && (
          <Column className="md:!flex-col-2 mr-4">
            <h4 className="font-semibold">{t('common:depositAmount')}</h4>
            <div className="space-x-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-10 flex">
              {cardMethods.map((notificationMethod) => (
                <div key={notificationMethod.id} className="flex items-center">
                  <input
                    id={notificationMethod.id}
                    type="radio"
                    {...register('type')}
                    value={notificationMethod.id}
                    defaultChecked={type === notificationMethod.id}
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                  />
                  <label
                    htmlFor={notificationMethod.id}
                    className="ml-3 block text-base font-medium text-gray-700"
                  >
                    {notificationMethod.title}
                  </label>
                </div>
              ))}
            </div>
          </Column>
        )}
        {isExternalAccess && <input type="hidden" {...register('type')} defaultValue="HOLD" />}
        <Column className="md:!flex-col-6 mb-0">
          {isExternalAccess ? (
            <AuthorizeOptions options={options} form={form} isExternalAccess={isExternalAccess} />
          ) : (
            <>
              {type === 'HOLD' ? (
                <AuthorizeOptions options={options} form={form} isExternalAccess={false} />
              ) : null}
              {type === 'CAPTURE' ? <CaptureOption form={form} /> : null}
            </>
          )}
        </Column>
      </Row>
    </StyledFormGroup>
  );
};

export default function DepositCardForm({ paymentMethods, depositOptions, onSubmit, cardErrors }) {
  const { t } = useTranslation();
  const { paymentMethodId } = getPreferredPaymentMethod(paymentMethods);
  const [{ type, amount }] = depositOptions;
  const isExternalAccess =
    typeof window !== 'undefined' && getUrlParam('userAccess') === 'external';

  const defaultValues = paymentMethodId
    ? {
        paymentMethodId,
        type: isExternalAccess ? 'HOLD' : type,
        amount,
        terms: false,
      }
    : {
        firstName: '',
        lastName: '',
        postalCode: '',
        cardNumber: '',
        cardExpiry: '',
        cardCvc: '',
        preferred: true,
        type: isExternalAccess ? 'HOLD' : type,
        amount: 1,
        terms: false,
      };

  const form = useDepositForm(defaultValues);
  const {
    watch,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = form;

  const cardError = cardErrors?.[0]?.message;

  const termsAccepted = watch('terms');
  const selectedPaymentMethodId = watch('paymentMethodId');
  const firstName = watch('firstName')?.trim();
  const lastName = watch('lastName')?.trim();
  const postalCode = watch('postalCode')?.trim();
  const cardNumber = watch('cardNumber');
  const cardExpiry = watch('cardExpiry');
  const cardCvc = watch('cardCvc');
  const depositType = watch('type');
  const depositAmount = watch('amount');

  const [isCheckoutDisabled, setIsCheckoutDisabled] = React.useState(true);
  React.useEffect(() => {
    const isNewCardComplete = Boolean(
      firstName && lastName && postalCode && cardNumber && cardExpiry && cardCvc
    );
    const hasValidAmount =
      depositAmount !== null &&
      depositAmount !== '' &&
      !Number.isNaN(Number(depositAmount)) &&
      Number(depositAmount) >= 0;
    setIsCheckoutDisabled(
      !termsAccepted || (!selectedPaymentMethodId && !isNewCardComplete) || !hasValidAmount
    );
  }, [
    termsAccepted,
    selectedPaymentMethodId,
    firstName,
    lastName,
    postalCode,
    cardNumber,
    cardExpiry,
    cardCvc,
    depositAmount,
  ]);

  const termsTextId = watch('type') === 'authorize' ? 'terms.card.hold' : 'terms.card.charge';

  const showPreferredCheckbox = (paymentMethods?.length ?? 0) > 0 && !selectedPaymentMethodId;

  React.useEffect(() => {
    if (!showPreferredCheckbox) {
      setValue('preferred', true);
    }
  }, [showPreferredCheckbox, setValue]);

  return (
    <form name="depositCardForm" onSubmit={handleSubmit(onSubmit)}>
      <StyledFormGroup error={cardError}>
        <CardSelector cardError={cardError} form={form} paymentMethods={paymentMethods}>
          <div className="flex mb-2 mt-2 justify-end">
            {showPreferredCheckbox ? (
              <Checkbox
                className="!mb-0"
                label={t('common.markAsPreferredCard')}
                name="preferred"
                register={register}
              />
            ) : null}
          </div>
        </CardSelector>
      </StyledFormGroup>

      <div className={isExternalAccess ? 'hidden' : undefined}>
        <DepositOptions options={depositOptions} form={form} isExternalAccess={isExternalAccess} />
      </div>
      <Card>
        <TermsInput
          name="terms"
          register={register}
          errors={errors}
          label={t('common.termsAndConditions')}
        >
          <p>{t(termsTextId)}</p>
        </TermsInput>
      </Card>

      <div className="flex flex-wrap justify-between items-end">
        <div className="md:pr-4 pr-0 w-full flex-col-12 md:!flex-col-6">
          <Card className="w-full">
            <div className="md:mt-0 flex-col-12 md:!flex-col-6 md:pt-2">
              <InputField
                register={register}
                label={t('common.receiptEmailOptional')}
                name={`email`}
                error={form.formState.errors?.email?.message}
              />
            </div>
          </Card>
        </div>

        {/* Desktop only: same checkout bar UI, part of flex layout */}
        <div className="w-full flex-col-12 md:!flex-col-6 hidden md:block">
          <Card className="w-full">
            <SecurePaymentNote />
            <CheckoutButton
              disabled={isCheckoutDisabled}
              depositType={depositType}
              amount={depositAmount}
            />
          </Card>
        </div>
      </div>

      {/* Mobile only: same checkout bar UI, sticky at bottom */}
      <div className="md:hidden sticky bottom-0 z-10 -mx-4 -mb-4 mt-4">
        <div className="px-4 pb-4 pt-4 bg-white/90 backdrop-blur-sm border-t border-gray-200/80 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]">
          <CheckoutButton
            disabled={isCheckoutDisabled}
            depositType={depositType}
            amount={depositAmount}
          />
          <SecurePaymentNote />
        </div>
      </div>
    </form>
  );
}
